// src/modules/system/ErrorLogList.js
//
// Admin view of everything that has crashed or errored — the iPhone app, the
// backend API, and background workers. One row per DISTINCT error (the API
// dedupes by fingerprint), so `occurrences` is how many times it actually fired.
//
// Pagination and filtering are SERVER-side, unlike the card-grid list pages in
// this app: this table can hold thousands of rows and grows fastest exactly
// when you most need it to stay responsive. Follows the pattern in
// modules/time-tracking/TimeTrackingReport.js.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaSyncAlt, FaCheck, FaBan, FaTrash, FaBug, FaFileCsv } from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import Pagination from '../../components/shared/Pagination';
import {
  StatusBadge, TableContainer, StyledTable, PageHeader, HeaderContent,
} from '../../components/styled/CommonStyles';
import ErrorStatsCards from './components/ErrorStatsCards';
import ErrorTrendChart from './components/ErrorTrendChart';
import ErrorFilters from './components/ErrorFilters';
import {
  listErrors, getErrorStats, bulkSetStatus, bulkDelete, exportCsv,
  severityColor, statusColor, statusLabel, sourceLabel, relativeTime,
} from './errorLogService';

const RefreshBtn = styled.button`
  background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  display: flex; align-items: center; gap: 8px; font-weight: 500;
`;
const GhostBtn = styled(RefreshBtn)`
  background: transparent; color: ${(p) => p.theme.colors.textSecondary};
  border: 1px solid ${(p) => p.theme.colors.inputGray};
  &[data-active='true'] { color: ${(p) => p.theme.colors.primary};
    border-color: ${(p) => p.theme.colors.primary}; }
`;
const ClickRow = styled.tr`
  cursor: pointer;
  td.msg { max-width: 460px; }
`;
const Message = styled.div`
  font-weight: 500; color: ${(p) => p.theme.colors.textPrimary};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const SubText = styled.div`
  color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.72rem; margin-top: 2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const Count = styled.span`
  font-weight: 700; color: ${(p) => p.theme.colors.textPrimary};
`;
const BulkBar = styled.div`
  display: flex; align-items: center; gap: ${(p) => p.theme.spacing(2)};
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(2)};
  span { color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.85rem; }
  button { display: flex; align-items: center; gap: 6px; cursor: pointer;
    background: transparent; border: 1px solid ${(p) => p.theme.colors.inputGray};
    color: ${(p) => p.theme.colors.textSecondary}; border-radius: ${(p) => p.theme.borderRadius};
    padding: 6px 12px; }
  button:hover { color: ${(p) => p.theme.colors.primary}; }
  button.danger:hover { color: ${(p) => p.theme.colors.error};
    border-color: ${(p) => p.theme.colors.error}; }

  /* Stick to the bottom on mobile: with a card list you scroll far from the
     top, and a bulk bar you have to scroll back up to reach is useless. */
  @media (max-width: 900px) {
    position: sticky; bottom: 0; z-index: 5; flex-wrap: wrap; gap: 8px;
    box-shadow: 0 -2px 8px rgba(0,0,0,.18);
    button { flex: 1 1 auto; justify-content: center; }
    span { width: 100%; }
  }
`;
const Empty = styled.div`
  text-align: center; padding: ${(p) => p.theme.spacing(6)};
  color: ${(p) => p.theme.colors.textSecondary};
  svg { font-size: 2.5rem; margin-bottom: ${(p) => p.theme.spacing(2)};
        color: ${(p) => p.theme.colors.primary}; opacity: .5; }
`;
const CheckCell = styled.td`width: 36px;
  input { width: 15px; height: 15px; accent-color: ${(p) => p.theme.colors.primary}; cursor: pointer; }
`;

/* ------------------------------- responsive -------------------------------
   The table has 9 columns. Below ~900px that stops being a table and starts
   being a horizontal-scroll puzzle, so under that width we swap it for a card
   list. Both are rendered and toggled with CSS media queries rather than a JS
   resize listener — no flash on first paint, no state to get out of sync. */

const DesktopTable = styled.div`
  @media (max-width: 900px) { display: none; }
`;
const MobileList = styled.div`
  display: none;
  @media (max-width: 900px) { display: block; }
`;
const ErrorCard = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border-radius: ${(p) => p.theme.borderRadius};
  border-left: 3px solid ${(p) => p.$accent || p.theme.colors.primary};
  padding: ${(p) => p.theme.spacing(2)};
  margin-bottom: ${(p) => p.theme.spacing(1.5)};
  box-shadow: 0 1px 3px rgba(0,0,0,.12);
  cursor: pointer;
  display: flex; gap: ${(p) => p.theme.spacing(1.5)};
  &:active { background: ${(p) => p.theme.colors.background}; }
`;
const CardCheck = styled.div`
  flex: 0 0 auto; padding-top: 2px;
  input { width: 18px; height: 18px; accent-color: ${(p) => p.theme.colors.primary}; }
`;
const CardBody = styled.div`flex: 1; min-width: 0;`;
const MobileToolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 ${(p) => p.theme.spacing(0.5)} ${(p) => p.theme.spacing(1)};
  color: ${(p) => p.theme.colors.textSecondary}; font-size: .76rem;
  label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
  input { width: 16px; height: 16px; accent-color: ${(p) => p.theme.colors.primary}; }
`;
const CardTop = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin-bottom: 6px;
`;
const CardMsg = styled.div`
  color: ${(p) => p.theme.colors.textPrimary}; font-weight: 500;
  font-size: .9rem; line-height: 1.35; word-break: break-word;
  /* Two lines is enough to recognize an error; more turns the list into a wall. */
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
`;
const CardWhere = styled.div`
  color: ${(p) => p.theme.colors.textSecondary}; font-size: .72rem; margin-top: 3px;
  word-break: break-all;
`;
const CardMeta = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 8px;
  color: ${(p) => p.theme.colors.textSecondary}; font-size: .72rem;
  strong { color: ${(p) => p.theme.colors.textPrimary}; font-weight: 700; }
`;

/* Header actions: 6 controls will not fit on one phone row. Let them wrap, and
   shrink the padding so they stay tappable without dominating the screen. */
const HeaderActions = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
  @media (max-width: 900px) {
    width: 100%; margin-top: ${(p) => p.theme.spacing(1)};
    button { flex: 1 1 auto; justify-content: center; padding: 8px 10px; font-size: .82rem; }
  }
`;

const EMPTY_FILTERS = {
  search: '', source: '', platform: '', severity: '', status: '',
  app_version: '', from: '', to: '',
};

const ErrorLogList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL so a view can be bookmarked and shared —
  // "here is the crash I mean" is a link, not a description.
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    ...Object.fromEntries(
      Object.keys(EMPTY_FILTERS)
        .map((k) => [k, searchParams.get(k) || ''])
        .filter(([, v]) => v)
    ),
  }));
  // Separate debounced copy so typing in Search doesn't fire a request per key.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const searchTimer = useRef(null);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(searchTimer.current);
  }, [filters.search]);

  // Mirror filters into the URL (replace, so the back button isn't flooded).
  useEffect(() => {
    const next = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) next[k] = v; });
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  // `search` is deliberately EXCLUDED from the query object and from load's
  // deps — it changes on every keystroke, and depending on it would fire one
  // request per character (each carrying the stale debounced value, each
  // blanking the table to a spinner, and racing each other on the way back).
  // Only `debouncedSearch` drives the fetch.
  const { search: _typedSearch, ...appliedFilters } = filters;
  const queryKey = JSON.stringify(appliedFilters);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const applied = JSON.parse(queryKey);
      const data = await listErrors({
        ...applied,
        search: debouncedSearch,
        // Include the whole "to" day.
        to: applied.to ? `${applied.to}T23:59:59` : '',
        limit: perPage,
        offset: (page - 1) * perPage,
      });
      setErrors(data.errors || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErrors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queryKey, debouncedSearch, page, perPage]);

  const loadStats = useCallback(async () => {
    try { setStats(await getErrorStats(days)); } catch (e) { setStats(null); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Opt-in polling — useful while watching a live crash, off by default so the
  // page isn't hammering the API all day in a background tab.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { load(); loadStats(); }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, load, loadStats]);

  const onFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
    setSelected([]);
  };
  const onClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setSelected([]);
  };

  const toggleOne = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOnPageSelected = errors.length > 0 && errors.every((e) => selected.includes(e.id));
  const toggleAll = () =>
    setSelected(allOnPageSelected ? [] : errors.map((e) => e.id));

  const feedback = (msg, ok = true) => {
    setFeedbackMsg(msg); setFeedbackSuccess(ok); setShowFeedback(true);
  };

  const runBulkStatus = async (status) => {
    try {
      const res = await bulkSetStatus(selected, status);
      setSelected([]);
      feedback(`${res.updated} error(s) marked ${statusLabel(status).toLowerCase()}`);
      load(); loadStats();
    } catch (e) {
      feedback(e.response?.data?.message || 'Failed to update errors', false);
    }
  };
  const runBulkDelete = async () => {
    setConfirmDelete(false);
    try {
      const res = await bulkDelete(selected);
      setSelected([]);
      feedback(`${res.deleted} error(s) deleted`);
      load(); loadStats();
    } catch (e) {
      feedback(e.response?.data?.message || 'Failed to delete errors', false);
    }
  };

  const appVersions = (stats?.byAppVersion || [])
    .map((r) => r.app_version).filter(Boolean);

  // Carry the active view into the detail URL so the detail page can step
  // through THIS filtered set with Prev/Next instead of sending you back here
  // after every error.
  const detailUrl = (id) => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
    q.append('_page', page);
    q.append('_per', perPage);
    const qs = q.toString();
    return `/system/errors/${id}${qs ? `?${qs}` : ''}`;
  };

  const runExport = async () => {
    try {
      const n = await exportCsv({ ...filters, search: debouncedSearch });
      feedback(`Exported ${n} error(s) to CSV`);
    } catch (e) {
      feedback('Export failed', false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <HeaderContent>
          <h1>System Errors</h1>
          <HeaderActions>
            <GhostBtn data-active={days === 1} onClick={() => setDays(1)}>24h</GhostBtn>
            <GhostBtn data-active={days === 7} onClick={() => setDays(7)}>7d</GhostBtn>
            <GhostBtn data-active={days === 30} onClick={() => setDays(30)}>30d</GhostBtn>
            <GhostBtn data-active={autoRefresh} onClick={() => setAutoRefresh((v) => !v)}>
              Auto {autoRefresh ? 'on' : 'off'}
            </GhostBtn>
            <GhostBtn onClick={runExport} title="Download the current filtered view as CSV">
              <FaFileCsv /> Export
            </GhostBtn>
            <RefreshBtn onClick={() => { load(); loadStats(); }}><FaSyncAlt /> Refresh</RefreshBtn>
          </HeaderActions>
        </HeaderContent>
      </PageHeader>

      <ErrorStatsCards stats={stats} />
      <ErrorTrendChart data={stats?.trend} />

      <ErrorFilters
        filters={filters}
        onChange={onFilterChange}
        onClear={onClearFilters}
        appVersions={appVersions}
      />

      {selected.length > 0 && (
        <BulkBar>
          <span>{selected.length} selected</span>
          <button onClick={() => runBulkStatus('resolved')}><FaCheck /> Resolve</button>
          <button onClick={() => runBulkStatus('ignored')}><FaBan /> Ignore</button>
          <button className="danger" onClick={() => setConfirmDelete(true)}><FaTrash /> Delete</button>
        </BulkBar>
      )}

      {loading ? <LoadingSpinner /> : errors.length === 0 ? (
        <TableContainer>
          <Empty>
            <div><FaBug /></div>
            <div>No errors match these filters.</div>
          </Empty>
        </TableContainer>
      ) : (
      <>
        {/* --- Phone / small tablet: card list --- */}
        <MobileList>
          {/* Select-all lives in the table header on desktop; without this the
              mobile view would silently lose bulk actions. */}
          <MobileToolbar>
            <label>
              <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} />
              Select all on page
            </label>
            <span>{total} error{total === 1 ? '' : 's'}</span>
          </MobileToolbar>
          {errors.map((e) => (
            <ErrorCard
              key={e.id}
              $accent={severityColor(e.severity)}
              onClick={() => navigate(detailUrl(e.id))}
            >
              <CardCheck onClick={(ev) => { ev.stopPropagation(); }}>
                <input type="checkbox" checked={selected.includes(e.id)}
                  onChange={() => toggleOne(e.id)} />
              </CardCheck>
              <CardBody>
                <CardTop>
                  <StatusBadge color={severityColor(e.severity)}>
                    {e.is_fatal ? 'crash' : e.severity}
                  </StatusBadge>
                  <StatusBadge color={statusColor(e.status)}>{statusLabel(e.status)}</StatusBadge>
                  <span style={{ marginLeft: 'auto', fontSize: '.7rem', opacity: .7 }}>
                    {relativeTime(e.last_seen_at)}
                  </span>
                </CardTop>
                <CardMsg>{e.message}</CardMsg>
                <CardWhere>
                  {sourceLabel(e.source)}{e.platform ? ` · ${e.platform}` : ''}
                  {e.error_type ? ` · ${e.error_type}` : ''}
                  {e.route ? ` · ${e.route}` : ''}
                </CardWhere>
                <CardMeta>
                  <span><strong>{e.occurrences}</strong>×</span>
                  {e.affected_user_count > 0 && <span><strong>{e.affected_user_count}</strong> users</span>}
                  {e.app_version && <span>v{e.app_version}</span>}
                  {e.os_version && <span>{e.os_name} {e.os_version}</span>}
                  {e.device_model && <span>{e.device_model}</span>}
                </CardMeta>
              </CardBody>
            </ErrorCard>
          ))}
          <Pagination
            itemsPerPage={perPage}
            totalItems={total}
            currentPage={page}
            paginate={setPage}
            itemsPerPageOptions={[25, 50, 100, 200]}
            onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          />
        </MobileList>

        {/* --- Desktop: full table --- */}
        <DesktopTable>
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll}
                    style={{ width: 15, height: 15, cursor: 'pointer' }} />
                </th>
                <th>Severity</th>
                <th>Where</th>
                <th>Error</th>
                <th>Count</th>
                <th>Users</th>
                <th>App / OS</th>
                <th>Last seen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((e) => (
                <ClickRow key={e.id} onClick={() => navigate(detailUrl(e.id))}>
                  <CheckCell onClick={(ev) => ev.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(e.id)}
                      onChange={() => toggleOne(e.id)} />
                  </CheckCell>
                  <td>
                    <StatusBadge color={severityColor(e.severity)}>
                      {e.is_fatal ? 'crash' : e.severity}
                    </StatusBadge>
                  </td>
                  <td>
                    <div>{sourceLabel(e.source)}</div>
                    {e.platform && <SubText>{e.platform}</SubText>}
                  </td>
                  <td className="msg">
                    <Message title={e.message}>{e.message}</Message>
                    <SubText title={e.route || e.url || ''}>
                      {e.error_type || '—'}{e.route ? ` · ${e.route}` : ''}
                      {e.status_code ? ` · ${e.status_code}` : ''}
                    </SubText>
                  </td>
                  <td><Count>{e.occurrences}</Count></td>
                  <td>{e.affected_user_count || 0}</td>
                  <td>
                    {e.app_version ? <div>{e.app_version}</div> : '—'}
                    {e.os_version && <SubText>{e.os_name || ''} {e.os_version}</SubText>}
                  </td>
                  <td title={e.last_seen_at}>{relativeTime(e.last_seen_at)}</td>
                  <td>
                    <StatusBadge color={statusColor(e.status)}>{statusLabel(e.status)}</StatusBadge>
                  </td>
                </ClickRow>
              ))}
            </tbody>
          </StyledTable>
          <Pagination
            itemsPerPage={perPage}
            totalItems={total}
            currentPage={page}
            paginate={setPage}
            itemsPerPageOptions={[25, 50, 100, 200]}
            onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          />
        </TableContainer>
        </DesktopTable>
      </>
      )}

      {confirmDelete && (
        <Modal
          isOpen={confirmDelete}
          title="Delete Errors"
          message={`Permanently delete ${selected.length} error log(s)? This cannot be undone. Deleting does not stop the error from recurring — "Ignore" is usually what you want.`}
          onConfirm={runBulkDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMsg}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default ErrorLogList;
